import { action, computed, observable, reaction, makeObservable } from 'mobx';
import React from 'react';
import {Disposers, logger} from '../../../helpers';
import {documentSize} from './document-size';
import {globalDragEvents} from './global-drag-events';
import {MovableId, MovableState, Rect, Size, XY} from './models';
import {Movable} from './Movable';
import {schd} from './raf-ratelimit';

let storeIdCounter = 0;

export class MovableStore {
  readonly storeId: number;
  readonly movableId: MovableId;

  readonly draggableRef: React.RefObject<any>;

  @observable
  isDragging: boolean;

  // coordinates of left-top point of draggable element in
  // set on dragging start
  @observable
  dragStartPosition: XY;

  // size of draggable
  @observable
  draggableSize: Size;

  // offset for mouse position in draggable element
  // set on drag start
  @observable
  inset: XY;

  // current mouse/touch position
  // set on mouse move
  @observable
  currentPosition: XY;

  // calculated position for mirror <br/>
  @observable
  mirrorPosition: XY;

  private dragCount = 0;

  private readonly disposers = new Disposers();

  constructor(readonly component: Movable) {
    makeObservable(this);
    this.storeId = storeIdCounter++;
    this.movableId = component.props.movableId;
    this.draggableRef = React.createRef<HTMLElement>();

    this.isDragging = false;
    this.dragStartPosition = {x: 0, y: 0};
    this.draggableSize = {width: 0, height: 0};
    this.currentPosition = {x: 0, y: 0};
    this.inset = {x: 0, y: 0};
    this.mirrorPosition = {x: 0, y: 0};

    this.setup();
  }

  @action
  onMouseDown = (event: MouseEvent) => {
    logger.log('MovableStore.onMouseDown');
    if (this.component.props.disabled)
      return;
    event.stopPropagation();
    if (this.isDragging || event.buttons !== 1 || event.ctrlKey || event.altKey || event.shiftKey) {
      this.cancelMove();
      return;
    }
    const pageXY: XY = {
      x: event.pageX,
      y: event.pageY
    };
    this.beginMove(pageXY);
  };

  @action
  onTouchStart = (event: TouchEvent) => {
    logger.log('MovableStore.onTouchStart');
    if (this.component.props.disabled)
      return;
    if (this.isDragging || event.touches.length !== 1) {
      this.cancelMove();
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    const touch = event.touches[0];
    const pageXY: XY = {x: touch.pageX, y: touch.pageY};
    this.beginMove(pageXY);
  };

  @action
  private beginMove = (pageXY: XY) => {
    this.cancelMove();
    if (this.draggableRef.current === null)
      return;
    this.dragStartPosition = {x: pageXY.x, y: pageXY.y};

    const scroll: XY = {x: window.scrollX, y: window.scrollY};
    let rect = (this.draggableRef.current as Element).getBoundingClientRect();
    this.draggableSize = {width: rect.width, height: rect.height};
    let draggableTopLeft: XY = {x: rect.left + scroll.x, y: rect.top + scroll.y};
    this.inset = {
      x: pageXY.x - draggableTopLeft.x,
      y: pageXY.y - draggableTopLeft.y
    };
    // isDragging must be set before calling moveTo
    this.isDragging = true;
    this.dragCount++;
    this.moveTo(this.dragCount, pageXY, false);

    globalDragEvents.beginMove({
      storeId: this.storeId,
      mouseDown: this.cancelMove,
      mouseUp: this.onMouseUp,
      mouseMove: this.onMouseMove,
      touchStart: this.onTouchStart,
      touchMove: this.onTouchMove,
      touchEnd: this.onTouchEnd
    });
    if (typeof this.component.props.onDragStart === 'function')
      this.component.props.onDragStart(this.state);
  };

  @action
  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isDragging)
      return;
    event.stopPropagation();
    if (event.buttons !== 1 || event.ctrlKey || event.altKey || event.shiftKey) {
      this.cancelMove();
      return;
    }
    const pageXY: XY = {x: event.pageX, y: event.pageY};
    const dragCount = this.dragCount;
    this.moveToSchd(dragCount, pageXY, true);
  };

  @action
  private onTouchMove = (event: TouchEvent): void => {
    if (!this.isDragging)
      return;
    event.stopPropagation();
    if (event.touches.length !== 1) {
      this.cancelMove();
      return;
    }
    const touch = event.touches[0];
    const pageXY: XY = {x: touch.pageX, y: touch.pageY};
    const dragCount = this.dragCount;
    this.moveToSchd(dragCount, pageXY, true);
  };

  @action
  private moveTo = (dragCount: number, pageXY: XY, callOnDragMove: boolean) => {
    if (!this.isDragging || this.dragCount !== dragCount) {
      this.cancelMove();
      return;
    }

    this.currentPosition = {x: pageXY.x, y: pageXY.y};
    let mirrorPosition: XY = {
      x: pageXY.x - this.inset.x,
      y: pageXY.y - this.inset.y
    };

    let size = documentSize();
    let bounds: Rect = {
      top: 0,
      left: 0,
      bottom: size.height,
      right: size.width
    };
    this.adjustBounds(bounds, mirrorPosition);
    this.mirrorPosition = mirrorPosition;

    if (typeof this.component.props.adjustMirrorPosition === 'function') {
      const calcResult = this.component.props.adjustMirrorPosition(this.state);
      if (calcResult !== null) {
        mirrorPosition.x = calcResult.x;
        mirrorPosition.y = calcResult.y;
        this.adjustBounds(bounds, mirrorPosition);
        this.mirrorPosition = mirrorPosition;
      }
    }
    if (callOnDragMove)
      if (typeof this.component.props.onDragMove === 'function')
        this.component.props.onDragMove(this.state);
  };

  private moveToSchd: (dragCount: number, pageXY: XY, callOnDragMove: boolean) => void =
    schd(this.moveTo);

  private adjustBounds(bounds: Rect,
                       mirrorPosition: XY): void {
    if (mirrorPosition.x + this.draggableSize.width > bounds.right)
      mirrorPosition.x = bounds.right - this.draggableSize.width;
    if (mirrorPosition.y + this.draggableSize.height > bounds.bottom)
      mirrorPosition.y = bounds.bottom - this.draggableSize.height;

    if (mirrorPosition.x < bounds.left)
      mirrorPosition.x = bounds.left;
    if (mirrorPosition.y < bounds.top)
      mirrorPosition.y = bounds.top;
  }

  @action
  private onMouseUp = (event: MouseEvent): void => {
    if (!this.isDragging)
      return;
    event.stopPropagation();
    if (event.buttons !== 0 || event.ctrlKey || event.altKey || event.shiftKey) {
      this.cancelMove();
      return;
    }
    const clientXY: XY = {x: event.pageX, y: event.pageY};
    this.moveTo(this.dragCount, clientXY, false);

    if (typeof this.component.props.onDragEnd === 'function')
      this.component.props.onDragEnd(this.state);
    globalDragEvents.endMove(this.storeId);
    this.isDragging = false;
  };

  @action
  private onTouchEnd = (event: TouchEvent): void => {
    if (!this.isDragging)
      return;
    event.stopPropagation();
    if (event.touches.length !== 0 && event.changedTouches.length !== 1) {
      this.cancelMove();
      return;
    }
    const touch = event.changedTouches[0];
    const clientXY: XY = {x: touch.pageX, y: touch.pageY};
    this.moveTo(this.dragCount, clientXY, false);

    if (typeof this.component.props.onDragEnd === 'function')
      this.component.props.onDragEnd(this.state);
    globalDragEvents.endMove(this.storeId);
    this.isDragging = false;
  };

  @action
  private cancelMove = () => {
    this.dragCount++;

    if (!this.isDragging)
      return;

    globalDragEvents.endMove(this.storeId);

    this.isDragging = false;
    if (typeof this.component.props.onDragCancel === 'function')
      this.component.props.onDragCancel(this.state);
  };

  @computed
  get state(): MovableState {
    return {
      movableId: this.movableId,
      dragStart: {x: this.dragStartPosition.x, y: this.dragStartPosition.y},
      draggableSize: {width: this.draggableSize.width, height: this.draggableSize.height},
      inset: {x: this.inset.x, y: this.inset.y},
      currentPosition: {x: this.currentPosition.x, y: this.currentPosition.y},
      mirrorPosition: {x: this.mirrorPosition.x, y: this.mirrorPosition.y},
      cancel: this.cancelMove
    };
  }

  @computed
  get mirrorStyle(): React.CSSProperties {
    if (!this.isDragging)
      return {};
    const scroll: XY = {x: window.scrollX, y: window.scrollY};
    let client: XY = {
      x: this.mirrorPosition.x - scroll.x,
      y: this.mirrorPosition.y - scroll.y
    };
    return {
      position: 'fixed',
      top: 0,
      left: 0,
      boxSizing: 'border-box',
      width: this.draggableSize.width,
      height: this.draggableSize.height,
      transform: `translate(${client.x}px, ${client.y}px)`,
      zIndex: 1000000,
      opacity: 100,
      pointerEvents: 'none'
    };
  }

  private setup() {
    this.disposers.add(reaction(
      () => this.component.props.disabled,
      () => {
        if (this.component.props.disabled === true)
          this.cancelMove();
      }));
  }

  @action
  dispose = () => {
    this.cancelMove();
    this.disposers.dispose();
  };
}
